import { Icon } from '../ui'
import { useAppStore } from '../../store/appStore'
import { useCapabilities } from '../../hooks/useCapabilities'
import { canSwitchRoles } from '../../lib/capabilities'
import { IPLP_FRAMEWORK, compStateFromRatings } from '../../lib/framework'
import { YearSwitcher } from './YearSwitcher'
import { AvatarMenu } from './AvatarMenu'
import type { ComponentRating, Evidence, Profile } from '../../lib/types'

interface SidebarProps {
  ratings?: ComponentRating[]
  evidence?: Evidence[]
  phase?: string
  profile: Profile | null
  onSignOut: () => void
}

function SmarkDot({ state }: { state: 'done' | 'part' | 'none' }) {
  return <span className={`smark ${state}`} />
}

const PHASE_DOTS: Record<string, string> = {
  fall: '#b3823a',
  midyear: '#2f6f82',
  spring: '#4a8a60',
}

/** Compact controls shown at the top of the mobile drawer (hidden on desktop via CSS). */
function MobileHeader({ profile, onSignOut }: { profile: Profile | null; onSignOut: () => void }) {
  const { role, phase, setRole, setPhase, setNav, setReviewId } = useAppStore()
  const caps = useCapabilities()
  return (
    <div className="side-mhead">
      {canSwitchRoles(caps) && (
        <div className="roleswitch rs-side">
          <button
            className={role === 'teacher' ? 'on' : ''}
            onClick={() => {
              setRole('teacher')
              setReviewId(null)
              setNav({ page: 'overview' })
            }}
          >
            <Icon name="user" size={14} /> Staff
          </button>
          <button
            className={role === 'admin' ? 'on' : ''}
            onClick={() => {
              setRole('admin')
              setReviewId(null)
              setNav({ page: 'dashboard' })
            }}
          >
            <Icon name="shield" size={14} /> Admin
          </button>
        </div>
      )}
      <div className="phasebar phasebar-side">
        {(['fall', 'midyear', 'spring'] as const).map((p) => (
          <button key={p} className={phase === p ? 'on' : ''} onClick={() => setPhase(p)}>
            <span className="ph-dot" style={{ background: PHASE_DOTS[p] }} />
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>
      <YearSwitcher />
      <AvatarMenu profile={profile} onSignOut={onSignOut} />
    </div>
  )
}

export function Sidebar({ ratings = [], evidence = [], phase = 'fall', profile, onSignOut }: SidebarProps) {
  const { role, nav, reviewId, drawerOpen, setNav, openSettings } = useAppStore()

  const isAdminRoot = role === 'admin' && !reviewId
  const isReviewing = role === 'admin' && !!reviewId

  if (isAdminRoot) {
    return (
      <nav className={`side${drawerOpen ? ' open' : ''}`} aria-label="Administration navigation">
        <MobileHeader profile={profile} onSignOut={onSignOut} />

        <div className="grp">
          <div className="grp-label">Administration</div>
          <button className={`navitem ${nav.page === 'dashboard' ? 'on' : ''}`} onClick={() => setNav({ page: 'dashboard' })}>
            <Icon name="grid" size={16} />
            Dashboard
          </button>
          <button className={`navitem ${nav.page === 'forms' ? 'on' : ''}`} onClick={() => setNav({ page: 'forms' })}>
            <Icon name="doc" size={16} />
            Forms &amp; Templates
          </button>
          <button className={`navitem ${nav.page === 'assign' ? 'on' : ''}`} onClick={() => setNav({ page: 'assign' })}>
            <Icon name="users" size={16} />
            Staff Assignment
          </button>
        </div>

        <div className="grp">
          <div className="grp-label">Quick filters</div>
          <button className="navitem" onClick={() => setNav({ page: 'dashboard' })}>
            <Icon name="clock" size={16} />
            Overdue
            <span className="count">3</span>
          </button>
          <button className="navitem" onClick={() => setNav({ page: 'dashboard' })}>
            <Icon name="eye" size={16} />
            Needs review
            <span className="count">5</span>
          </button>
        </div>

        <div className="grp">
          <div className="grp-label">Configuration</div>
          <button className="navitem" onClick={() => openSettings('district')}>
            <Icon name="settings" size={16} />
            District Settings
          </button>
          <button className="navitem" onClick={() => openSettings('notifications')}>
            <Icon name="bell" size={16} />
            Notifications
          </button>
          <button className="navitem" onClick={() => openSettings('profile')}>
            <Icon name="user" size={16} />
            Account
          </button>
        </div>
      </nav>
    )
  }

  // Teacher or admin reviewing
  const sectionLabel = isReviewing ? 'Reviewing' : 'My IPLP'
  const activeDomain = nav.page === 'domain' ? nav.domain : null
  const activeCode = nav.page === 'domain' ? nav.code : null

  return (
    <nav className={`side${drawerOpen ? ' open' : ''}`} aria-label="IPLP navigation">
      <MobileHeader profile={profile} onSignOut={onSignOut} />

      <div className="grp">
        <div className="grp-label">{sectionLabel}</div>
        <button className={`navitem ${nav.page === 'overview' ? 'on' : ''}`} onClick={() => setNav({ page: 'overview' })}>
          <Icon name="home" size={16} />
          Overview
        </button>
        <button className={`navitem ${nav.page === 'summary' ? 'on' : ''}`} onClick={() => setNav({ page: 'summary' })}>
          <Icon name="layout" size={16} />
          Summary
        </button>
      </div>

      <div className="grp">
        <div className="grp-label">Domains</div>
        {IPLP_FRAMEWORK.map((domain) => {
          const isDomainActive = activeDomain === domain.n
          return (
            <div key={domain.n}>
              <button
                className={`navitem ${isDomainActive && !activeCode ? 'on' : ''}`}
                onClick={() => setNav({ page: 'domain', domain: domain.n, code: domain.components[0].code })}
              >
                <Icon name="book" size={16} />
                <span style={{ flex: 1, textAlign: 'left', fontSize: 13 }}>
                  {domain.n}. {domain.title}
                </span>
              </button>

              {isDomainActive && (
                <div style={{ paddingLeft: 12, marginBottom: 4 }}>
                  {domain.components.map((comp) => {
                    const state = compStateFromRatings(ratings, evidence, comp.code, phase)
                    return (
                      <button
                        key={comp.code}
                        className={`navitem ${activeCode === comp.code ? 'on' : ''}`}
                        style={{ fontSize: 13, padding: '7px 10px' }}
                        onClick={() => setNav({ page: 'domain', domain: domain.n, code: comp.code })}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--mono)',
                            fontSize: 11,
                            fontWeight: 600,
                            color: activeCode === comp.code ? 'rgba(255,255,255,.7)' : 'var(--ink-3)',
                            width: 22,
                            flexShrink: 0,
                          }}
                        >
                          {comp.code}
                        </span>
                        <span style={{ flex: 1, textAlign: 'left', lineHeight: 1.25 }}>{comp.title}</span>
                        <SmarkDot state={state} />
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="grp">
        <div className="grp-label">More</div>
        <button className={`navitem ${nav.page === 'goals' ? 'on' : ''}`} onClick={() => setNav({ page: 'goals' })}>
          <Icon name="target" size={16} />
          Goals
        </button>
        <button className={`navitem ${nav.page === 'sections' ? 'on' : ''}`} onClick={() => setNav({ page: 'sections' })}>
          <Icon name="pen" size={16} />
          Sections &amp; Sign-off
        </button>
        <button className={`navitem ${nav.page === 'history' ? 'on' : ''}`} onClick={() => setNav({ page: 'history' })}>
          <Icon name="clock" size={16} />
          Plan History
        </button>
      </div>
    </nav>
  )
}
